import { useState, useEffect, Fragment } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Dropdown from 'react-bootstrap/Dropdown'

import ErrorMessage from '../components/psc/lib/ErrorMessage'
import SaveButton from '../components/psc/lib/SaveButton'
import CancelButton from '../components/psc/lib/CancelButton'
import DeleteButton from '../components/psc/lib/DeleteButton'
import MultiSelect from '../components/psc/lib/MultiSelect'
import TextArea from '../components/psc/lib/TextArea'
import Checkbox from '../components/psc/lib/Checkbox'

import useAuthentication from '../hooks/useAuthentication';
import useProjectTypes from '../hooks/useProjectTypes';
import useProjectAccessLevels from '../hooks/useProjectAccessLevels';
import useRoleManagement from '../hooks/useRoleManagement';
import useUserManagement from '../hooks/useUserManagement';
import useUsergroupManagement from '../hooks/useUsergroupManagement';

const Usergroup = () => {
    const navigate = useNavigate();
    const params = useParams();
    const isNew = params.objID === 'new';
    const objID = Number(params.objID);

    const { currentUser } = useAuthentication();

    const { projectTypes } = useProjectTypes()
    const { projectAccessLevels } = useProjectAccessLevels() 
    const { allRoles, roleGroups } = useRoleManagement()

    const { 
        activeUsers,
        userHasRole,
        forceReloadUsers,
    } = useUserManagement()

    const { 
        reloadUsergroups,
    } = useUsergroupManagement()

    const [ id, setID ] = useState(null)
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [selectedUsers, setSelectedUsers] = useState([])
    const [roles, setRoles] = useState([])
    const [projects, setProjects] = useState([])
    const [usergroupProjectAccess, setUsergroupProjectAccess] = useState([])
    const [error, setError] = useState(null)
    const [submitActive, setSubmitActive] = useState(false)
    const [allProjects, setAllProjects] = useState([])

    useEffect(() => {
        fetch('/v0/projects?is_archived=0', { credentials: 'same-origin' })
            .then(function(response) {
                return response.json()
            })
            .then(function(json) {
                let projects = json.results.map(function (item) {
                    let res = {
                        id: item.id,
                        project_id: item.id,
                        project_type_id: item.project_type_id,
                        value: item.id,
                        label: item.project_code + ' - ' + item.name,
                        user_project_access_level_id: 1 // default when added
                    }
                    return res
                })
                setAllProjects(projects)
            })

        if (isNew) {
            return
        }
        
        fetch('/v0/usergroups/' + objID, { credentials: 'same-origin' })
            .then(response => response.json())
            .then(json => {
                let data = json.result
                setID(data.id)
                setName(data.name)
                setDescription(data.description)
                setRoles(data.roles || [])
                setSelectedUsers(data.users)
                setUsergroupProjectAccess(data.usergroup_project_access || [])
            })
        
    }, [isNew, objID])

    useEffect(() => {
        // Translate project ids ( in projectAccess ) to list of projects
        let list = []
        usergroupProjectAccess.forEach(function (item) {
            let project = allProjects.find(function (p) {
                return item.project_id === p.id
            })
            if( !project ) {
                return
            }
            list.push({
                id: project.id,
                value: project.id,
                label: project.label,
                user_project_access_level_id: item.user_project_access_level_id,
                project_id: project.id,
                project_type_id: project.project_type_id,
                user_id: id
            })
        })

        list.sort(function (a,b) {
            if (a.label < b.label) {
                return -1;
            }
            if (a.label > b.label) {
                return 1;
            }
            // names must be equal
            return 0;
        })

        setProjects(list)
    }, [usergroupProjectAccess, allProjects, id])

    const toBackend = () => {
        let data = {
            name: name,
            description: description,
            roles: roles,
            users: selectedUsers,
            usergroup_project_access: projects.map(function (item) {
                let res = {
                    project_id: item.project_id,
                    user_project_access_level_id: item.user_project_access_level_id
                }
                if( !isNew ) {
                    res.usergroup_id = id
                }
                return res
            })
        }
        if( !isNew ) {
            data.id = id
        }
        return data
    }

    const validateBeforeSubmit = () => {
        const errors = []
        if (!name) {
            errors.push('Name is required')
        }
        if (name && name.length > 32) {
            errors.push('Name can have a maximum of 32 characters')
        }
        return errors
    }

    const handleSubmitError = (props) => {
        setSubmitActive(false)
        setError([ props.message ])
    }

    const handleSubmit = () => {
        if( submitActive || !validateBeforeSubmit() ) {
            return null
        }

        setSubmitActive(true)

        return {
            objType: 'usergroups',
            objID: objID,
            data: toBackend(),
            successCallback: async () => {
                await forceReloadUsers()
                await reloadUsergroups()
            }
        }
    }

    const handleDelete = async () => {
        await forceReloadUsers()
        await reloadUsergroups()
    }

    const addAllProjects = (project_type_id, level_id) => {
        const current_level = {}
        projects.forEach(i => {
            current_level[i.id] = i.user_project_access_level_id
        })

        const newProjects = allProjects
            .filter(item => item.project_type_id === project_type_id)
            .map(item => ({
                ...item,
                user_project_access_level_id: current_level[item.id] || level_id
            }))

        const otherProjects = projects
            .filter(item => item.project_type_id !== project_type_id)

        const combined = [...newProjects, ...otherProjects]
            .sort((a, b) => a.label.localeCompare(b.label))

        setProjects(combined)
    }

    const removeAllProjects = (project_type_id) => {
        const filtered = projects.filter(
            item => item.project_type_id !== project_type_id
        )
        setProjects(filtered)
    }

    const toggleRoleCheckbox = (item) => {
        // If exists, remove. Otherwise add
        const exists = roles.find(role => role.id === item.id) ? true : false
        if( exists ) {
            const updated = roles.filter(role => role.id !== item.id)
            setRoles(updated)
            return
        }
        const updated = [ ...roles, item ]
        setRoles(updated)
    }

    const createRoleCheckbox = (item) => {
        if (['normal_user', 'user_admin', 'full_access_admin'].includes(item.code)) {
            return null
        }

        const checked = !!roles.find(role => role.id === item.id)
        const disabled = false

        return (
            <div className="form-check" key={item.id}>
                <Checkbox
                    id={item.id}
                    name={item.id}
                    onChange={() => toggleRoleCheckbox(item)}
                    isChecked={checked}
                    disabled={disabled}
                />
                <label htmlFor={item.id}>
                    {item.name}
                    <span className="help-block m-b-none">
                        {item.description}
                    </span>
                </label>
            </div>
        )
    }

    const handleChangeRoleGroup = (group, role) => {
        // clear all in the group
        let update = roles.filter(item => item.role_group_id !== group.id)
        
        // now if there is a role, check it
        if(role) {
            update.push(role)
        }
        setRoles(update)
    }

    const createRoleGroup = (group) => {
        if( ! allRoles ) { return }

        let hasAnyRole = false
        let members = allRoles.filter(function (role) {
            return role.role_group_id === group.id
        }).map(function (role) {
            let className = 'btn btn-sm btn-white'
            if( roles.find(r => r.code === role.code)) {
                className = 'btn btn-sm btn-primary'
                hasAnyRole = true
            }
            return (
                <button
                    className={className}
                    type="button"
                    key={role.id}
                    value={role.id}
                    onClick={() => handleChangeRoleGroup(group, role)}
                    data-tooltip-id="gnctooltip" data-tooltip-content={role.tooltip}
                >
                    {role.name}
                </button>
            )
        })

        let noneClassName = ( hasAnyRole ) ? 'btn btn-sm btn-white' : 'btn btn-sm btn-primary'
        let none = (
            <button
                className={noneClassName}
                type="button"
                key="none"
                value="none"
                onClick={() => handleChangeRoleGroup(group)}
                data-tooltip-id="gnctooltip" data-tooltip-content="Cannot access this area"
            >
                None
            </button>
        )

        return (
            <tr key={group.id}>
                <td className="col-md-4">
                    {group.name}
                </td>
                <td className="col-md-4">
                    <div className="btn-group">
                        {none}
                        {members}
                    </div>
                </td>
            </tr>
        )
    }

    const renderUsers = () => {
        return (
            <>
            <div className="form-group" key="users-1">
                <label className="col-sm-2"></label>
                <div className="col-sm-8">
                    <h3 className="users-header">Users</h3>
                    <p>This group includes the following users.</p>
                </div>
            </div>
            <div className="form-group">
                <label className="col-sm-2 control-label"></label>
                <div className="col-sm-8">
                   <MultiSelect
                        options={activeUsers}
                        value={selectedUsers}
                        onChange={handleChangeUsers}
                        clearable={false}
                    />
                </div>
            </div>
            <div className="form-group">
                <label className="col-sm-2 control-label"></label>

                <div className="col-sm-8">
                    <table className="table">
                        <tbody>
                            {selectedUsers.map(function(item) {
                                 return (
                                     <tr key={item.id}>
                                         <td className="col-md-4">
                                             {item.label}
                                         </td>
                                         <td className="col-md-4">
                                         </td>
                                     </tr>
                                 )
                             })
                            }
                        </tbody>
                    </table>
                </div>
            </div>
            </>
        );
    }
    
    const handleChangeUsers = (newUsers) => {
        setSelectedUsers(newUsers)
    }

    const renderRoles = () => (
        <>
            <div className="form-group" key="roles-1">
                <label className="col-sm-2"></label>
                <div className="col-sm-8">
                    <h3 className="roles-header">Roles</h3>
                    <p>This group has the following system and administration roles.</p>
                </div>

            </div>
            <div className="form-group" key="plain-roles">
                <label className="col-sm-2"></label>
                <div className="col-sm-8">
                    {allRoles
                        .filter((role) => role.role_group_id === null)
                        .map(createRoleCheckbox)
                    }
                </div>
            </div>
            <div className="form-group" key="empty-row-1">
                <label className="col-sm-2"></label>
                <div className="col-sm-8">
                </div>
            </div>
            <div className="form-group" key="quality-1">
                <label className="col-sm-2"></label>
                <div className="col-sm-8">
                    <h3 className="quality-header">Quality Access</h3>
                    <p>This group has the following quality information access.</p>
                </div>
            </div>
            <div className="form-group" key="group-roles">
                <label className="col-sm-2"></label>
                <div className="col-sm-8">
                    <table className="table">
                        <tbody>
                            {roleGroups?.map(createRoleGroup)}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="form-group" key="empty-row-2">
                <label className="col-sm-2"></label>
                <div className="col-sm-8">
                </div>
            </div>
        </>
    )

    const handleChangeProjectAccess = (project_type_id, newProjects) => {
        const sortedProjects = [...newProjects].sort((a, b) => 
            a.label.localeCompare(b.label)
        )

        // Keep all projects with other project types
        const otherProjects = projects.filter(item => 
            item.project_type_id !== project_type_id
        )

        setProjects([...sortedProjects, ...otherProjects])
    }

    const handleChangeProjectAccessLevel = (project, level_id) => {
        const updatedProjects = projects.map(item => ({
            ...item,
            user_project_access_level_id: item.id === project.id ? level_id : item.user_project_access_level_id
        }))
        setProjects(updatedProjects)
    }

    const renderAllProjectAccess = () => {
        if ( ! projects || ! allProjects || ! projectTypes) {
            return (<div />)
        }
        return projectTypes.map(renderProjectAccess);
    }

    const renderProjectAccess = (projectType) => {
        const correctProjectType = (item) => item.project_type_id === projectType.id;
        const filteredAllProjects = allProjects.filter(correctProjectType);
        const filteredProjects = projects.filter(correctProjectType);

        return (
            <Fragment key={`${projectType.id}-${projectType.name}`}>
            <div className="form-group" style={{marginBottom: 0}}>
                <label className="col-sm-2"></label>
                <div className="col-sm-8">
                    <h3 className={`${projectType.name.toLowerCase()}-header`}>{projectType.name} Access</h3>
                    <p>
                        This group has the following access.
                    </p>
                    <div className="btn-group" style={{float: 'right'}}>
                        <Dropdown>
                            <Dropdown.Toggle size="xs" variant="white">
                                Add All As <b className="caret"></b>
                            </Dropdown.Toggle>

                            <Dropdown.Menu>
                                {projectAccessLevels.map((i) => {
                                     return (
                                         <Dropdown.Item key={i.code}>
                                             <button className="dropdown-item" href="#" onClick={() => addAllProjects(projectType.id, i.id)}>
                                                 {i.name}
                                             </button>
                                         </Dropdown.Item>
                                     )
                                 })
                                }
                            </Dropdown.Menu>
                        </Dropdown>
                        <button type="button" className="btn btn-white btn-xs"
                            onClick={() => removeAllProjects(projectType.id)}>
                            Remove All
                        </button>
                    </div>
                </div>
            </div>
            <div className="form-group">
                <label className="col-sm-2 control-label"></label>
                <div className="col-sm-8">
                    <MultiSelect
                        options={filteredAllProjects}
                        value={filteredProjects}
                        onChange={(value) => handleChangeProjectAccess(projectType.id, value)}
                        clearable={false}
                    />
                </div>
            </div>
            <div className="form-group">
                <label className="col-sm-2 control-label"></label>

                <div className="col-sm-8">
                    <table className="table">
                        <tbody>
                            {filteredProjects.map(function(item) {
                                 return (
                                     <tr key={item.id}>
                                         <td className="col-md-4">
                                             {item.label}
                                         </td>
                                         <td className="col-md-4">
                                             <div className="btn-group">
                                                 {projectAccessLevels.map(function (i) {
                                                      let className = ( item.user_project_access_level_id === i.id ) ? 'btn btn-sm btn-primary' : 'btn btn-sm btn-white'
                                                      return (
                                                          <button
                                                              className={className}
                                                              type="button"
                                                              key={i.id}
                                                              value={i.id}
                                                              onClick={() => handleChangeProjectAccessLevel(item, i.id)}
                                                              data-tooltip-id="gnctooltip" data-tooltip-content={i.tooltip}
                                                          >
                                                              {i.name}
                                                          </button>
                                                      )
                                                  })
                                                 }
                                             </div>
                                         </td>
                                     </tr>
                                 )
                             })
                            }
                        </tbody>
                    </table>
                </div>
            </div>
            </Fragment>
        )
    }

    if (!userHasRole(currentUser,'user_admin')) {
        navigate('/main')
        return null
    }

    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            <h2>User Group: {name || '<New Group>'}</h2>

                        </div>
                        <div className="ibox-content">
                            <form className="form-horizontal" onSubmit={(event) => event.preventDefault()}>
                                <div className="form-group">
                                    <label className="col-sm-2 control-label required">Name</label>
                                    <div className="col-sm-8"><input className="form-control" type="text" name="name" value={name} onChange={(e) => setName(e.target.value)} maxLength="32" required /></div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Description
                                    </label>
                                    <div className="col-sm-8">
                                        <TextArea className="form-control" name="description" value={description} onChange={(e) => setDescription(e.target.value)} />
                                    </div>
                                </div>
                                
                                <div className="hr-line-dashed"></div>

                                {renderUsers()}
                                
                                <div className="hr-line-dashed"></div>

                                {renderRoles()}

                                {renderAllProjectAccess()}

                                <div className="hr-line-dashed"></div>

                                <div className="form-group">
                                    <ErrorMessage
                                        error={error}
                                    />
                                    
                                    <div className="col-sm-10 col-sm-offset-2">
                                        <CancelButton/>

                                        <SaveButton
                                            buttonVariant='primary'
                                            visible
                                            spinnerActive={submitActive}
                                            label='Save Changes'
                                            onClick={handleSubmit}
                                            onError={handleSubmitError}
                                        />
                                        
                                        {!isNew && (
                                            <DeleteButton
                                                key="delete-button"
                                                url={`/v0/usergroups/${objID}`}
                                                setError={setError}
                                                callback={handleDelete}
                                            />
                                        )}
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Usergroup
